import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import Layout from '../components/layout';
import Title from '../components/title';
import Statscard from '../components/statscard';
import './users.css';
import searchIcon from '../assets/search.svg';
import dropdownIcon from '../assets/dropdown.svg';
import editIcon from '../assets/edit.svg';
import deleteIcon from '../assets/delete.svg';
import usersIcon from '../assets/users.svg';
import peopleIcon from '../assets/people.svg';
import ratingIcon from '../assets/rating.svg';

const USERS_PER_PAGE = 2;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('All Roles');
  const [currentPage, setCurrentPage] = useState(1);
  const [id, setId] = useState('');
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('');
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);

  const fetchUsers = async () => {
    const res = await supabase
      .from("Users")
      .select("*");

    setUsers(res.data);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const editUser = (user) => {
    setId(user.id);
    setEditName(user.name);
    setEditEmail(user.email);
    setEditRole(user.role);
    setIsEditUserOpen(true);
  };

  const updateUser = async (e) => {
    e.preventDefault();

    await supabase
      .from("Users")
      .update({
        name: editName,
        email: editEmail,
        role: editRole,
      })
      .eq('id', id);

    await fetchUsers();
    setIsEditUserOpen(false);
  };

  const deleteUser = async (id) => {
    await supabase.from("Users").delete().eq('id', id);
    await fetchUsers();
  };

  const roleOptions = [...new Set(users.map((u) => u.role))];

  const filteredUsers = users.filter((user) => {
    const value = searchTerm.toLowerCase();

    const matchesRole =
      selectedRole === 'All Roles' ||
      user.role === selectedRole;

    const matchesSearch = (
      user.id.toString().includes(value) ||
      user.name?.toLowerCase().includes(value) ||
      user.email?.toLowerCase().includes(value) ||
      user.role?.toLowerCase().includes(value)
    );

    return matchesSearch && matchesRole;
  });

  const totalUsers = users.length;
  const adminUsers = users.filter((u) => u.role === 'admin').length;
  const regularUsers = users.filter((u) => u.role === 'user').length;

  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const startIndex = (currentPage - 1) * USERS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + USERS_PER_PAGE);

  return (
    <Layout>
      <div className="users_page">
        <Title
          subtitle="Manage all users on the Eventra platform"
          title="USERS MANAGEMENT"
        />

        <div className="users_stats">
          <Statscard
            change=""
            icon={usersIcon}
            label="Total Users"
            value={totalUsers}
          />
          <Statscard
            change=""
            icon={ratingIcon}
            label="Admin Users"
            value={adminUsers}
          />
          <Statscard
            change=""
            icon={peopleIcon}
            label="Regular Users"
            value={regularUsers}
          />
        </div>

        <div className="users_controls_card">
          <div className="users_controls_left">
            <label className="users_search" htmlFor="users-search">
              <img alt="" src={searchIcon} />
              <input
                id="users-search"
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search users..."
                type="text"
                value={searchTerm}
              />
            </label>

            <div className="users_filter">
              <select
                onChange={(e) => {
                  setSelectedRole(e.target.value);
                  setCurrentPage(1);
                }}
                value={selectedRole}
              >
                <option value="All Roles">All Roles</option>
                {roleOptions.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
              <img alt="" src={dropdownIcon} />
            </div>
          </div>
        </div>

        <div className="users_table_card">
          <div className="users_table_top">
            <div className="users_table_title">All Users</div>
            <div className="users_table_count">{filteredUsers.length} total users</div>
          </div>

          <div className="users_table_scroll">
            <div className="users_row users_table_head">
              <div className="users_checkbox">
                <input type="checkbox" />
              </div>
              <div>ID</div>
              <div>Created At</div>
              <div>Name</div>
              <div>Email</div>
              <div>Role</div>
              <div>Actions</div>
            </div>

            <div className="users_table_body">
              {paginatedUsers.map((user) => (
                <div className="users_row" key={user.id}>
                  <div className="users_checkbox">
                    <input type="checkbox" />
                  </div>

                  <div className="users_cell_text">{user.id}</div>
                  <div className="users_cell_text">{user.created_at}</div>
                  <div className="users_cell_text">{user.name}</div>
                  <div className="users_cell_text">{user.email}</div>
                  <div className="users_cell_text">{user.role}</div>

                  <div className="users_actions">
                    <button
                      className="users_action_button"
                      onClick={() => editUser(user)}>
                      <img alt="" src={editIcon} />
                    </button>
                    <button className="users_action_button" onClick={() => deleteUser(user.id)}>
                      <img alt="" src={deleteIcon} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {totalPages > 1 && (
            <div className="users_pagination">
              <button
                className="users_pagination_button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              <div className="users_pagination_info">
                Page {currentPage} of {totalPages}
              </div>
              <button
                className="users_pagination_button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {isEditUserOpen && (
          <div
            className="users_modal_overlay"
            onClick={() => setIsEditUserOpen(false)}
            role="presentation"
          >
            <div
              aria-modal="true"
              className="users_modal"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
            >
              <div className="users_modal_header">
                <div className="users_modal_heading">
                  <h2>EDIT USER</h2>
                  <p>Update the user details below.</p>
                </div>

                <button
                  aria-label="Close edit user overlay"
                  className="users_modal_close"
                  onClick={() => setIsEditUserOpen(false)}>
                  x
                </button>
              </div>

              <form
                className="users_modal_form"
                onSubmit={updateUser}
              >
                <div className="users_modal_grid">
                  <label className="users_modal_field">
                    <span>Name</span>
                    <input
                      onChange={(e) => setEditName(e.target.value)}
                      type="text"
                      value={editName}
                    />
                  </label>

                  <label className="users_modal_field">
                    <span>Email</span>
                    <input
                      onChange={(e) => setEditEmail(e.target.value)}
                      type="text"
                      value={editEmail}
                    />
                  </label>

                  <label className="users_modal_field users_modal_field_full">
                    <span>Role</span>
                    <div className="users_modal_select_wrap">
                      <select
                        onChange={(e) => setEditRole(e.target.value)}
                        value={editRole}
                      >
                        {roleOptions.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                      <img alt="" src={dropdownIcon} />
                    </div>
                  </label>
                </div>

                <div className="users_modal_actions">
                  <button
                    className="users_modal_secondary_button"
                    onClick={() => setIsEditUserOpen(false)}>
                    Cancel
                  </button>
                  <button className="users_modal_primary_button" type="submit">
                    Update User
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Users;
